/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useMemo, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {Tooltip, Icon} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {AddIcon} from '../icons'

// Components
import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    Stack,
    useMultiStyleConfig,
    IconButton,
    Flex,
    Button,
    useBreakpointValue,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    useDisclosure,
    Heading,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import Badges from '../product-badges'

// Lazy load QuickView - only loaded when user clicks to quick view a product
// This defers Chakra Modal/Drawer components and product-view until needed
const QuickView = loadable(() => import('../../components/quick-view'), {ssr: false})

// Project Components
import {InfoIcon} from '@salesforce/retail-react-app/app/components/icons'
import {HeartIcon, HeartSolidIcon} from '../icons'
import Link from '@salesforce/retail-react-app/app/components/link'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import withRegistration from '../with-registration'
import ProductLikes from './product-likes'
import Video from '../video'

// Hooks
import {useIntl} from 'react-intl'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {usePromotions, useAccessToken, useProduct} from '@salesforce/commerce-sdk-react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {useVariationByAttributeValues} from '../../hooks/use-variant-by-attribute-values'
import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

// Other
import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {productUrlBuilder, rebuildPathWithParams} from '../../utils/url'
import {getPriceData} from '../../utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {filterImageGroups} from '@salesforce/retail-react-app/app/utils/product-utils'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {PRODUCT_BADGE_DETAILS} from '@salesforce/retail-react-app/app/constants'
import DisplayPrice from '../display-price'
import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {renderComponentsSwitch} from '../optimizely/utils/contentRenderer'
import {RESOURCE_BADGE_MAPPING} from '../../constants'
import {getIsFixedPriceModel} from '../../esw/esw-helpers'
import {shouldHideSaleBadgeForLocale} from '../../utils/sale-badges'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    getBasketItemInventoryFieldsForLocale,
    getProductRequestParametersForLocale
} from '../../../../utils/intl-inventory'

import {sendItemCartEvent, sendClickEvent} from '../../utils/gtm-utils'
import {sendBrazeCartEvent} from '../../utils/braze-utils'

const IconButtonWithRegistration = withRegistration(IconButton, {isWishlistAction: true})

// Component Skeleton
const PricingAndPromotionsSkeleton = () => {
    return (
        <Stack spacing={0} data-testid="sf-product-tile-pricing-and-promotions-skeleton">
            <ChakraSkeleton width="80px" height="18px" />
            <ChakraSkeleton width={{base: '120px', md: '220px'}} height="18px" />
        </Stack>
    )
}

export const Skeleton = () => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box data-testid="sf-product-tile-skeleton">
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <PricingAndPromotionsSkeleton />
            </Stack>
        </Box>
    )
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = React.memo(
    forwardRef((props, ref) => {
        const location = useLocation()
        const isFixedPriceModel = getIsFixedPriceModel()

        const {
            dynamicImageProps,
            videoProps,
            enableFavourite = true,
            imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE,
            isFavourite,
            onFavouriteToggle,
            product = {},
            selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID,
            badgeDetails = PRODUCT_BADGE_DETAILS,
            isRefreshingData = false,
            showSwatches,
            showVideo = false,
            showProductLikes = true,
            showQV = true,
            isShopTheLook = false,
            isOOS = false,
            setQuickViewProduct,
            setQuickViewWishlistInfo,
            setMobileQuickViewOpen,
            isWishlistPage = false,
            disableImageScroll = false,
            overwriteProductName = null,
            responsiveMediaBlock = null,
            variant: stylingVariant,
            onClick,
            isLayout = false,
            // Extract CMS styling props to prevent them from being passed to DOM
            productTitleStyle,
            productPriceStyle,
            ...rest
        } = props
        const {productId: initialProductId, id, representedProduct, productType} = product
        const {locale: currentLocale} = useMultiSite()
        const isProductSet = productType?.set === true || product?.type?.set === true
        // For variation groups, productId may be undefined, so we use id as fallback
        // This ensures QuickView can fetch product data correctly
        const effectiveInitialProductId = initialProductId || id
        const [productId, setProductId] = useState(effectiveInitialProductId)

        const {data: fetchedProduct} = useProduct(
            {
                parameters: {
                    id: productId,
                    ...getProductRequestParametersForLocale(currentLocale),
                    ...(currentLocale?.isSupportedByESW ? {perPricebook: true} : {}),
                    expand: ['availability', 'images', 'prices', 'promotions', 'variations']
                }
            },
            {
                keepPreviousData: true
            }
        )

        const {imageGroups, variants} = useMemo(() => {
            const effectiveProduct = fetchedProduct || product
            return {
                imageGroups: effectiveProduct?.imageGroups || [],
                variants: effectiveProduct?.variants || []
            }
        }, [fetchedProduct, product])

        // Store the ORIGINAL productId for wishlist removal (the one from the wishlist item)
        // This is the product.id value that never changes, even when user selects different variants
        const wishlistProductId = id

        // Ensure productId is always valid - if it becomes undefined, reset it
        useEffect(() => {
            if (!productId && effectiveInitialProductId) {
                setProductId(effectiveInitialProductId)
            }
        }, [productId, effectiveInitialProductId])
        const {isOpen, onOpen, onToggle, onClose} = useDisclosure()
        const {onOpen: onAddToCartModalOpen, onClose: onAddToCartModalClose} = useMiniCartContext()
        const {showCustomToast} = useCustomToast()
        const navigate = useNavigation()

        const intl = useIntl()
        const {currency} = useCurrency()

        const config = getConfig()

        const isDesktop = useBreakpointValue({base: false, lg: true})

        const [currentProduct, setCurrentProduct] = useState(product)
        const [isHovered, setIsHovered] = useState(false)
        const [isQuickViewOpen, setIsQuickViewOpen] = useState(false)

        // Mobile Quickview State
        const [mobileQuickViewOpen, setMobileQuickViewOpenInternal] = useState(false)

        // Track variant selected in QuickView to update tile price
        const [selectedVariantId, setSelectedVariantId] = useState(null)

        // Track product selected in QuickView
        const [qvProduct, setQvProduct] = useState(null)

        // Wishlist hook
        const {isInWishlist, toggleWishlist, removeItemFromWishlist} = useWishlistActions()

        // Styles
        const styles = useMultiStyleConfig('ProductTile', {
            variant: stylingVariant
        })

        const hasLengthVariant = Array.isArray(currentProduct?.variants)
            ? currentProduct.variants.some(
                  (variant) =>
                      variant?.variationValues &&
                      typeof variant.variationValues === 'object' &&
                      'length' in variant.variationValues &&
                      variant.variationValues.length
              )
            : false

        const isMasterVariant = !!variants
        const initialVariationValue =
            isMasterVariant && !!representedProduct
                ? variants?.find(
                      (variant) => variant.productId === currentProduct.representedProduct.id
                  )?.variationValues?.[selectableAttributeId]
                : undefined

        // I added this because the representedProduct is available only on the useProductSearch hook so the price will not be properly rendered
        const defaultColorVariationValue =
            isMasterVariant && !initialVariationValue
                ? currentProduct?.variationValues?.color
                : undefined

        let [colorValue, setColorValue] = useState(
            initialVariationValue || defaultColorVariationValue
        )
        let [wishlistCount, setWishlistCount] = useState(0)
        const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false)
        const [shouldFetchFreshLikes, setShouldFetchFreshLikes] = useState(false)
        const queryClient = useQueryClient()

        const productObject = fetchedProduct || product

        useEffect(() => {
            if (fetchedProduct) {
                setCurrentProduct((prev) => ({
                    ...prev,
                    ...fetchedProduct
                }))
            }
        }, [fetchedProduct])

        useEffect(() => {
            setWishlistCount(productObject?.c_wishlistCount || 0)
        }, [productObject])

        // Client-side only: Fetch fresh wishlist count on hover to bypass cache
        // As per BD team specification: use c_cp=1 to ensure non-cached near real-time data
        const isClient = typeof window !== 'undefined'

        // For PLP products, we need to get the correct master product ID
        // Products can be: master (has variants), variationGroup, or variant
        // Priority: master.masterId > id > productId
        const masterId =
            productObject?.master?.masterId ||
            productObject?.id ||
            productObject?.productId ||
            id ||
            productId

        // Reset fetch state when product/masterId changes (e.g., navigating to different PLP without reload)
        // This ensures hover will trigger a fresh fetch for each unique product
        useEffect(() => {
            setShouldFetchFreshLikes(false)
        }, [masterId])

        // Reset fetch state and invalidate cache when route changes (SPA navigation)
        // This handles the case where user interacts with a product, navigates away, and comes back
        // Without this, React Query's cache prevents fetching fresh data on hover after navigation
        useEffect(() => {
            setShouldFetchFreshLikes(false)
            // Invalidate the cache for this specific product so React Query will fetch fresh data
            queryClient.invalidateQueries(['product-wishlist-count', masterId])
        }, [location.pathname, queryClient, masterId])

        // Fetch fresh product data with c_cp=1 and select parameters (BD specification)
        // Using direct fetch because commerce-sdk-react's useProduct doesn't properly pass
        // the select parameter to the API (it gets stripped/ignored by the SDK)
        const {app: appConfig} = getConfig()
        const {getTokenWhenReady} = useAccessToken()
        const appOrigin = useAppOrigin()

        const {data: freshProductData, isLoading: isLoadingFreshLikes} = useQuery(
            ['product-wishlist-count', masterId],
            async () => {
                if (!masterId) return null

                // Get the access token for authentication
                const token = await getTokenWhenReady()

                // Build the API URL exactly as BD specified (working in Postman)
                // Note: We manually build the query string to avoid URLSearchParams encoding parentheses
                // The select parameter must be: (data.(c_wishlistCount)) not %28data.%28c_wishlistCount%29%29
                const {proxyPath, parameters} = appConfig.commerceAPI
                const organizationId = parameters.organizationId

                const queryParams = [
                    `select=(data.(c_wishlistCount))`,
                    `ids=${masterId}`,
                    `currency=${appConfig.defaultCurrency || 'GBP'}`,
                    `locale=${appConfig.defaultLocale || 'en-GB'}`,
                    `perPricebook=true`,
                    `siteId=${appConfig.siteId || 'DynamiteGarageUK'}`,
                    `c_cp=1`
                ].join('&')

                const url = `${appOrigin}${proxyPath}/product/shopper-products/v1/organizations/${organizationId}/products?${queryParams}`

                const response = await fetch(url, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: token ? `Bearer ${token}` : ''
                    }
                })

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const result = await response.json()
                // API returns { data: [{ c_wishlistCount: X }] }
                return result?.data?.[0] || null
            },
            {
                enabled: isClient && shouldFetchFreshLikes && Boolean(masterId),
                staleTime: 30 * 1000, // Match BD backend cache: 30 seconds
                cacheTime: 30 * 1000, // Cache each product independently for 30 seconds
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                // Allow refetch when component remounts with same product (navigation without reload)
                refetchOnReconnect: false,
                // Keep previous data while fetching to avoid flickering
                keepPreviousData: false
            }
        )

        // Update wishlist count when fresh data arrives
        useEffect(() => {
            if (freshProductData?.c_wishlistCount !== undefined) {
                setWishlistCount(freshProductData.c_wishlistCount)
            }
        }, [freshProductData])

        const isWishlistEnabled = productObject?.c_enableWishlistCount || false

        // Skip QuickView (show "Add to bag" button) ONLY for true variant products in Wishlist
        // - Variants: have type.variant === true (specific color/size already selected)
        // - Masters/VariationGroups: have type.variationGroup === true or type.master === true (need attribute selection)
        const isActualVariant = currentProduct?.type?.variant === true

        // Check if product is a Gift Card (physical or electronic)
        const isGiftCard =
            productObject?.c_isGiftCard ||
            productObject?.c_isEGiftCard ||
            productObject?.representedProduct?.c_isGiftCard ||
            productObject?.representedProduct?.c_isEGiftCard

        const ShouldSkipQuickView = (isActualVariant && isWishlistPage) || isGiftCard

        let enableFinalSaleLabel =
            !currentLocale?.isSupportedByESW &&
            (config.app.ENABLE_FINAL_SALE_LABEL === true ||
                config.app.ENABLE_FINAL_SALE_LABEL === 'true' ||
                productObject?.c_isEGiftCard ||
                productObject?.c_isGiftCard ||
                productObject?.representedProduct?.c_isEGiftCard ||
                productObject?.representedProduct?.c_isGiftCard)

        // Determine wishlist status
        const isProductInWishlist =
            isFavourite !== undefined ? isFavourite : isInWishlist(productId || id)

        // Handle favourite toggle
        const handleFavouriteClick = async () => {
            if (onFavouriteToggle) {
                // Use external handler (como ProductList faz)
                await onFavouriteToggle(!isProductInWishlist)
            } else {
                // Use internal logic (fallback automático)
                const productObj = {productId: productId || id, ...currentProduct}
                await toggleWishlist(productObj, ref?.current)
            }

            // Update local count
            if (isProductInWishlist) {
                setWishlistCount((prev) => Math.max(0, prev - 1))
            } else {
                setWishlistCount((prev) => prev + 1)
            }
        }

        // Primary image for the tile, the image is determined from the product and selected variation attributes.
        const image = useMemo(() => {
            // CASE 1: PLP without allImages - only root `image` field is available (no imageGroups)
            // This is the most performant option for PLP
            if (!imageGroups?.length && product?.image) {
                // Return the root image directly - no second image for hover
                return [
                    {
                        disBaseLink: product.image.disBaseLink,
                        link: product.image.link,
                        alt: product.image.alt,
                        title: product.image.title
                    },
                    {} // No hover image available without allImages
                ]
            }

            // NOTE: If the selectable variation attribute doesn't exist in the products variation attributes
            // array, lets not filter the image groups on it. This ensures we always return an image for non-variant
            // type products.
            const hasSelectableAttribute = product?.variationAttributes?.find(
                ({id}) => id === selectableAttributeId
            )

            // Check if imageGroups have variationAttributes (PLP with allImages=true do, Vicky carousel products don't)
            const imageGroupsHaveVariationAttributes = imageGroups?.some(
                (group) => group.variationAttributes?.length > 0
            )

            // CASE 2: Vicky carousel products - imageGroups exist but WITHOUT variationAttributes
            // Just filter by viewType and return the first match
            if (imageGroups?.length > 0 && !imageGroupsHaveVariationAttributes) {
                const filteredImageGroups = filterImageGroups(imageGroups, {
                    viewType: imageViewType,
                    variationValues: {}
                })
                const matchedImageGroup = filteredImageGroups?.[0]
                return [{...matchedImageGroup?.images?.[0]}, {...matchedImageGroup?.images?.[1]}]
            }

            // CASE 3: PLP with allImages=true - imageGroups have variationAttributes for color matching
            // For variant products (e.g., in Wishlist), use the specific color from variationValues
            // This ensures we show the correct color image that the user saved
            const isVariant = currentProduct?.type?.variant
            const variantColor = isVariant ? currentProduct?.variationValues?.color : null

            // Extract color from root image URL (e.g., "100092730_04N_1920x2880.jpg" → "04N")
            // This is the API's default color selection and should be used as the primary source of truth
            let colorFromRootImage = null
            if (product?.image?.disBaseLink || product?.image?.link) {
                const imageUrl = product.image.disBaseLink || product.image.link
                // Match pattern: productId_colorCode_dimensions.jpg
                const match = imageUrl.match(/\/(\d+)_([A-Z0-9]+)(?:_alt\d+)?_\d+x\d+\.jpg/i)
                if (match && match[2]) {
                    colorFromRootImage = match[2]
                }
            }

            // Fallback: extract color code from the productId
            // Example: productId "10009273004N" → color code "04N"
            let colorFromProductId = null
            if (currentProduct?.type?.variationGroup && !variantColor && !colorValue) {
                // Extract last 3-4 characters as color code (matches pattern like 04N, 1CV, 06G)
                const currentId = productId || product?.id
                if (currentId) {
                    // Try to find the color in the available imageGroups
                    const colorMatch = imageGroups
                        ?.filter((g) => g.viewType === 'large')
                        ?.map((g) => g.variationAttributes?.[0]?.values?.[0]?.value)
                        ?.find((color) => currentId.toUpperCase().endsWith(color?.toUpperCase()))
                    colorFromProductId = colorMatch || null
                }
            }

            // Determine which color to use for filtering (priority order):
            // 1. Variant product with specific color (variationValues.color)
            // 2. User-selected color (colorValue state)
            // 3. Color from root image field (API's default selection)
            // 4. Color extracted from productId (for variation groups)
            // 5. Custom property c_color
            const effectiveColorValue =
                variantColor ||
                colorValue ||
                colorFromRootImage ||
                colorFromProductId ||
                product?.c_color

            const variationValues = effectiveColorValue
                ? {[selectableAttributeId]: effectiveColorValue}
                : {[selectableAttributeId]: initialVariationValue}

            const filteredImageGroups = filterImageGroups(imageGroups, {
                viewType: imageViewType,
                variationValues: hasSelectableAttribute ? variationValues : {}
            })

            let matchedImageGroup

            // Try to match by color variation attribute
            if (effectiveColorValue && filteredImageGroups?.length > 0) {
                matchedImageGroup = filteredImageGroups.find((group) =>
                    group.variationAttributes?.some(
                        (attr) =>
                            attr.id === 'color' &&
                            attr.values?.some((val) => val.value === effectiveColorValue)
                    )
                )
            }

            // Fallback to first filtered image group
            if (!matchedImageGroup && filteredImageGroups?.length > 0) {
                matchedImageGroup = filteredImageGroups[0]
            }

            return [{...matchedImageGroup?.images?.[0]}, {...matchedImageGroup?.images?.[1]}]
        }, [
            currentProduct,
            selectableAttributeId,
            initialVariationValue,
            imageViewType,
            colorValue
        ])

        // Primary URL user to wrap the ProduceTile.
        // Note: We no longer include color in URL params for SEO purposes.
        // Color is determined by the variation group productId in the URL path.
        const productUrl = useMemo(() => {
            // If QuickView has fetched full product data, use it (has c_relativeUrl)
            if (qvProduct) return productUrlBuilder(qvProduct)

            // If productId was updated by QuickView swatch click (immediate),
            // reconstruct the URL from the current product's URL pattern
            let productForUrl = product
            if (product?.c_relativeUrl && productId) {
                const urlMatch = product.c_relativeUrl.match(/^(.*\/)([^/]+)\.html$/)
                const urlProductId = urlMatch?.[2]
                if (urlProductId && urlProductId !== productId) {
                    productForUrl = {
                        ...product,
                        c_relativeUrl: `${urlMatch[1]}${productId}.html`
                    }
                }
            }

            return productUrlBuilder(productForUrl)
        }, [product, qvProduct, productId])

        // NOTE: variationAttributes are only defined for master/variant type products.
        // const variationAttributes = useMemo(() => getDecoratedVariationAttributes(product), [product])

        // ProductTile is used by two components, RecommendedProducts and ProductList.
        // RecommendedProducts provides a localized product name as `name` and non-localized product
        // name as `productName`. ProductList provides a localized name as `productName` and does not
        // use the `name` property.
        const localizedProductName =
            overwriteProductName || (currentProduct.name ?? currentProduct.productName)
        const productWithFilteredVariants = useMemo(() => {
            const variants = currentProduct?.variants?.filter(
                ({variationValues}) =>
                    variationValues?.[selectableAttributeId] ===
                    (initialVariationValue || defaultColorVariationValue)
            )
            return {
                ...currentProduct,
                variants
            }
        }, [
            currentProduct,
            selectableAttributeId,
            initialVariationValue,
            defaultColorVariationValue
        ])

        // Pricing is dynamic! Ensure we are showing the right price for the selected variation attribute
        // value. When a variant is selected in QuickView, use that variant's price data.
        const priceData = useMemo(() => {
            // If a variant is selected in QuickView, calculate price for that specific variant
            if (selectedVariantId && currentProduct?.variants) {
                const selectedVariant = currentProduct.variants.find(
                    (v) => v.productId === selectedVariantId
                )
                if (selectedVariant) {
                    // Use variant's own promotions, or fallback to product-level promotions
                    const variantWithPromotions = {
                        ...selectedVariant,
                        productPromotions:
                            selectedVariant.productPromotions || currentProduct?.productPromotions
                    }
                    return getPriceData(variantWithPromotions)
                }
            }
            // No variant selected - show lowest price across all variants
            return getPriceData(productWithFilteredVariants)
        }, [
            productWithFilteredVariants,
            selectedVariantId,
            currentProduct?.variants,
            currentProduct?.productPromotions
        ])

        // Retrieve product badges
        const filteredLabels = useMemo(() => {
            const labelsMap = new Map()
            if (currentProduct?.representedProduct) {
                badgeDetails.forEach((item) => {
                    if (
                        item.propertyName &&
                        typeof currentProduct.representedProduct[item.propertyName] === 'boolean' &&
                        currentProduct.representedProduct[item.propertyName] === true
                    ) {
                        labelsMap.set(intl.formatMessage(item.label), item.color)
                    }
                })
            }
            return labelsMap
        }, [currentProduct, badgeDetails])

        const handleNotifyMeModalState = (stateValue) => {
            setIsNotifyMeModalOpen(stateValue)
        }

        const showQuickView = () => {
            if (isDesktop) {
                setIsQuickViewOpen(true)
            } else {
                if (setMobileQuickViewOpen) {
                    setQuickViewProduct(productId)
                    setQuickViewWishlistInfo({
                        wishlistCount: wishlistCount,
                        isFavourite: isProductInWishlist
                    })
                    setMobileQuickViewOpen(true)
                } else {
                    setMobileQuickViewOpenInternal(true)
                }
            }
        }

        const variationValues = productWithFilteredVariants?.variationValues || []

        const variant = useVariationByAttributeValues(currentProduct, variationValues)

        const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
        const basketItemInventoryFields = getBasketItemInventoryFieldsForLocale(currentLocale)

        const addProductToCart = async (productSelectionValues) => {
            try {
                const productItems = productSelectionValues.map(({variant}) => ({
                    productId: variant.productId,
                    price: variant.price,
                    ...basketItemInventoryFields,
                    quantity: 1
                }))

                const basket = await addItemToNewOrExistingBasket(productItems)

                // GTM Modification
                sendItemCartEvent(productItems[0], false)

                // Braze Modification - Send Braze Cart Event
                sendBrazeCartEvent(basket, productSelectionValues[0]?.currentProduct, 'add')
                return productSelectionValues
            } catch (error) {
                console.error('The error occured while adding a product to the basket: ', error)
            }
        }

        // Handle cart item addition with validation
        const handleCartItem = async () => {
            try {
                const itemsAdded = await addProductToCart([{currentProduct, variant}])

                if (itemsAdded) {
                    // Remove from wishlist if on wishlist page
                    if (isWishlistPage) {
                        const productObj = {productId: productId || id, ...currentProduct}
                        await removeItemFromWishlist(
                            productObj,
                            ref?.current,
                            'add-to-cart-context'
                        )
                    }

                    setIsQuickViewOpen(false)
                    if (isDesktop) {
                        onAddToCartModalOpen()
                        setTimeout(() => {
                            onAddToCartModalClose()
                        }, 2000)
                    } else {
                        setTimeout(() => {
                            showCustomToast({
                                message: 'Added to Bag',
                                action: (
                                    <Button
                                        variant="link"
                                        fontWeight="medium"
                                        fontSize="sm"
                                        onClick={() => navigate('/cart')}
                                        colorScheme="white"
                                        textDecoration="underline"
                                    >
                                        {intl.formatMessage({
                                            id: 'add_to_cart_modal.link.view_bag',
                                            defaultMessage: 'View Bag'
                                        })}
                                    </Button>
                                ),
                                styles: {
                                    position: 'relative',
                                    top: 'auto',
                                    width: '100%',
                                    right: 0,
                                    left: 0
                                },
                                containerId: 'app-header'
                            })
                        }, 500)
                    }
                }
            } catch (e) {
                console.log(e)
            }
        }

        const handleMouseEnter = () => {
            if (!isOOS) {
                setIsHovered(true)
            }
            // Trigger fresh likes count fetch on hover to bypass cache (BD specification)
            if (!shouldFetchFreshLikes) {
                setShouldFetchFreshLikes(true)
            }
        }

        const handleMouseLeave = () => {
            if (!isNotifyMeModalOpen && !isOOS) {
                setIsHovered(false)
                setIsQuickViewOpen(false)
            }
        }

        useEffect(() => {
            if (!isNotifyMeModalOpen) {
                setIsHovered(false)
                setIsQuickViewOpen(false)
            }
        }, [isNotifyMeModalOpen])

        const hideQuickViewMobile = () => {
            setMobileQuickViewOpenInternal(false)
        }

        useEffect(() => {
            window.addEventListener('resize', hideQuickViewMobile)
            return () => window.removeEventListener('resize', hideQuickViewMobile)
        }, [])

        const renderSingleColorSwatch = () => {
            if (!currentProduct?.c_tilesSwatches) {
                return null
            }

            const {count, swatchURL} = currentProduct.c_tilesSwatches

            return (
                <Flex alignItems="baseline">
                    <SwatchGroup ariaLabel="Colors">
                        <Swatch
                            key="main-color"
                            value="color"
                            name="Color"
                            isProductTile
                            variant={'circle'}
                            w="10px"
                            h="10px"
                        >
                            <Box
                                height="100%"
                                width="100%"
                                backgroundRepeat="no-repeat"
                                backgroundSize="cover"
                                backgroundImage={`url(${swatchURL})`}
                            />
                        </Swatch>
                    </SwatchGroup>
                    {count > 0 && (
                        <Text
                            fontWeight="medium"
                            fontSize={12}
                            whiteSpace="nowrap"
                            lineHeight="15px"
                            mt="-2.5px"
                        >
                            +{count}
                        </Text>
                    )}
                </Flex>
            )
        }

        // Strip query parameters before adding dynamic image params
        const stripQueryParams = (url) =>
            url && typeof url === 'string' ? url.split('?')[0] : null

        const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED =
            appConfig?.PRODUCT_IMGS_RESIZE_PARAMS_ENABLED === 'true'

        const mainImageUrl = image[0]?.disBaseLink || image[0]?.link
        const mainImage = mainImageUrl
            ? PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
                ? `${stripQueryParams(mainImageUrl)}[?sw={width}&q=60]`
                : stripQueryParams(mainImageUrl)
            : ''

        const hoveredImageUrl = image[1]?.disBaseLink || image[1]?.link
        const hoveredImage = hoveredImageUrl
            ? PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
                ? `${stripQueryParams(hoveredImageUrl)}[?sw={width}&q=60]`
                : stripQueryParams(hoveredImageUrl)
            : null
        const showHoverImage = hoveredImage && isHydrated() && !isQuickViewOpen

        const promotionIds = getProductPromotionIds(productWithFilteredVariants)
        const {data} = usePromotions(
            {
                parameters: {
                    ids: promotionIds.join(',')
                }
            },
            {
                enabled:
                    !currentLocale?.isSupportedByESW &&
                    !isFixedPriceModel &&
                    promotionIds.length > 0
            }
        )
        const productPromotionObject =
            !currentLocale?.isSupportedByESW && !isFixedPriceModel
                ? data?.data?.find((promo) => promo.c_showCalloutMessage === true)
                : null

        const onQuickViewCloseHandler = () => {
            setIsHovered(false)
        }

        const renderProductCallout = () => {
            return productObject?.representedProduct?.c_textBadges
                ?.filter((badge) => !shouldHideSaleBadgeForLocale(currentLocale, badge))
                .map((badge) => (
                    <Text
                        key={badge}
                        color="grey.700"
                        {...styles.bodyText}
                        {...styles.productTileLabel}
                    >
                        {intl.formatMessage(RESOURCE_BADGE_MAPPING[badge])}
                    </Text>
                ))
        }

        return (
            <>
                <Box
                    {...styles.container}
                    {...rest}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onFocus={handleMouseEnter}
                    ref={ref}
                    position="relative"
                >
                    <Link
                        opacity={isOOS ? '0.5' : '1'}
                        pointerEvents={isOOS ? 'none' : 'auto'}
                        data-testid="product-tile"
                        to={productUrl}
                        {...styles.link}
                        onClick={onClick}
                    >
                        {productObject?.representedProduct?.c_mediaProductTile && showVideo ? (
                            <Box {...styles.imageWrapper}>
                                <Badges badges={productObject?.c_badges} />
                                <AspectRatio {...styles.image} pb={0}>
                                    <Video
                                        autoPlay
                                        muted
                                        src={productObject?.representedProduct?.c_mediaProductTile}
                                        loop
                                        playsInline
                                        preload={videoProps?.preload ?? 'metadata'}
                                    />
                                </AspectRatio>
                            </Box>
                        ) : responsiveMediaBlock ? (
                            <Box {...styles.imageWrapper}>
                                <Box
                                    {...styles.image}
                                    pb={0}
                                    overflowX={disableImageScroll ? 'hidden' : 'auto'}
                                    css={{
                                        '&::-webkit-scrollbar': {
                                            display: 'none'
                                        }
                                    }}
                                >
                                    {isLayout ? (
                                        <Box {...styles.responsiveMediaBlock}>
                                            {renderComponentsSwitch(responsiveMediaBlock)}
                                        </Box>
                                    ) : (
                                        <AspectRatio
                                            {...styles.image}
                                            {...styles.responsiveMediaBlock}
                                        >
                                            {renderComponentsSwitch(responsiveMediaBlock)}
                                        </AspectRatio>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box {...styles.imageWrapper}>
                                <Badges badges={productObject?.c_badges} />
                                <Box
                                    {...styles.image}
                                    pb={0}
                                    overflowX={disableImageScroll ? 'hidden' : 'auto'}
                                    css={{
                                        '&::-webkit-scrollbar': {
                                            display: 'none'
                                        }
                                    }}
                                >
                                    <Box
                                        width={showHoverImage ? {base: '200%', lg: 'full'} : 'full'}
                                        display={'flex'}
                                    >
                                        <AspectRatio
                                            {...styles.image}
                                            transition="opacity 0.2s ease-in-out"
                                            pb={0}
                                        >
                                            <DynamicImage
                                                data-testid="product-tile-image"
                                                src={mainImage}
                                                widths={dynamicImageProps?.widths}
                                                imageProps={{
                                                    alt:
                                                        productObject?.productName ||
                                                        productObject?.name,
                                                    loading:
                                                        dynamicImageProps?.imageProps?.loading ??
                                                        'lazy',
                                                    ...dynamicImageProps?.imageProps,
                                                    width: '100%'
                                                }}
                                            />
                                        </AspectRatio>
                                        {showHoverImage && (
                                            <AspectRatio
                                                {...styles.image}
                                                {...styles.hoveredImageContainer}
                                                transition="opacity 0.2s ease-in-out"
                                                opacity={isHovered || !isDesktop ? 1 : 0}
                                            >
                                                <DynamicImage
                                                    data-testid="product-tile-hover-image"
                                                    src={hoveredImage}
                                                    widths={dynamicImageProps?.widths}
                                                    imageProps={{
                                                        alt:
                                                            productObject?.productName ||
                                                            productObject?.name,
                                                        loading: 'lazy',
                                                        decoding: 'async',
                                                        fetchpriority: 'low',
                                                        width: '100%'
                                                    }}
                                                />
                                            </AspectRatio>
                                        )}
                                    </Box>
                                </Box>
                                {!ShouldSkipQuickView &&
                                    showQV &&
                                    !isGiftCard &&
                                    !isProductSet &&
                                    isHydrated() && (
                                        <Flex
                                            position="absolute"
                                            bottom="0"
                                            width="full"
                                            p={1}
                                            flexDirection="column"
                                            zIndex="2"
                                            opacity={isHovered || !isDesktop ? '1' : '0'}
                                        >
                                            {isQuickViewOpen ? (
                                                <QuickView
                                                    isNotifyMeModalStateOpen={
                                                        handleNotifyMeModalState
                                                    }
                                                    setIsQuickViewOpen={setIsQuickViewOpen}
                                                    productId={productId}
                                                    setColorValue={setColorValue}
                                                    setProductId={setProductId}
                                                    onQuickViewClose={onQuickViewCloseHandler}
                                                    isWishlistPage={isWishlistPage}
                                                    wishlistProductId={wishlistProductId}
                                                    hasLengthVariant={hasLengthVariant}
                                                    searchHitData={product}
                                                    onVariantSelect={setSelectedVariantId}
                                                    onProductChange={setQvProduct}
                                                />
                                            ) : (
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                        showQuickView()
                                                        //GTM Modification
                                                        sendClickEvent('quick_add_clicked')
                                                    }}
                                                    width={isDesktop ? '100%' : '24px'}
                                                    height={isDesktop ? '100%' : '24px'}
                                                    variant="quickViewBtn"
                                                    minWidth="auto"
                                                    marginLeft="auto"
                                                    p={isDesktop ? null : '2px'}
                                                    right={isDesktop ? null : '1'}
                                                    bottom={isDesktop ? null : '1'}
                                                    data-testid="quick-add"
                                                >
                                                    <HideOnMobile>
                                                        <FormattedMessage
                                                            defaultMessage="Quick Add"
                                                            id="product_tile.button.quick_add"
                                                        />
                                                    </HideOnMobile>
                                                    <AddIcon
                                                        w="20px"
                                                        h="20px"
                                                        boxSize={4}
                                                        position={isDesktop ? 'absolute' : 'static'}
                                                        right={isDesktop ? '10px' : 'auto'}
                                                        top={isDesktop ? '50%' : 'auto'}
                                                        transform={
                                                            isDesktop ? 'translateY(-50%)' : 'auto'
                                                        }
                                                    />
                                                </Button>
                                            )}
                                        </Flex>
                                    )}

                                {ShouldSkipQuickView &&
                                    isHydrated() &&
                                    showQV &&
                                    !isGiftCard &&
                                    !isProductSet && (
                                        <Flex
                                            position="absolute"
                                            bottom="0"
                                            width="full"
                                            p={1}
                                            flexDirection="column"
                                            zIndex="2"
                                            opacity={isHovered || !isDesktop ? '1' : '0'}
                                        >
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    handleCartItem()
                                                }}
                                                width={isDesktop ? '100%' : '24px'}
                                                height={isDesktop ? '100%' : '24px'}
                                                variant="quickViewBtn"
                                                minWidth="auto"
                                                marginLeft="auto"
                                                p={isDesktop ? null : '2px'}
                                                right={isDesktop ? null : '1'}
                                                bottom={isDesktop ? null : '1'}
                                            >
                                                <HideOnMobile>
                                                    <FormattedMessage
                                                        defaultMessage="Add to bag"
                                                        id="product_tile.button.add_to_bag"
                                                    />
                                                </HideOnMobile>
                                                <AddIcon
                                                    w="20px"
                                                    h="20px"
                                                    boxSize={4}
                                                    position={isDesktop ? 'absolute' : 'static'}
                                                    right={isDesktop ? '10px' : 'auto'}
                                                    top={isDesktop ? '50%' : 'auto'}
                                                    transform={
                                                        isDesktop ? 'translateY(-50%)' : 'auto'
                                                    }
                                                />
                                            </Button>
                                        </Flex>
                                    )}
                            </Box>
                        )}
                        <Box>
                            {/* Title */}
                            <Flex
                                {...styles.bodyText}
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                {isShopTheLook ? (
                                    <Heading
                                        as="h3"
                                        variant="plpH3"
                                        {...styles.title}
                                        sx={
                                            isProductSet
                                                ? {
                                                      position: 'relative',
                                                      '&::after': {
                                                          content: '""',
                                                          display: 'block',
                                                          position: 'absolute',
                                                          bottom: 0,
                                                          left: 0,
                                                          width: isHovered ? '100%' : '0%',
                                                          height: '1px',
                                                          backgroundColor: 'currentColor',
                                                          transition: 'width 200ms ease-in-out'
                                                      }
                                                  }
                                                : undefined
                                        }
                                        data-testid="product-name"
                                    >
                                        {localizedProductName}
                                    </Heading>
                                ) : (
                                    <Heading
                                        as="h2"
                                        variant="plpH2"
                                        {...styles.title}
                                        sx={
                                            isProductSet
                                                ? {
                                                      position: 'relative',
                                                      '&::after': {
                                                          content: '""',
                                                          display: 'block',
                                                          position: 'absolute',
                                                          bottom: 0,
                                                          left: 0,
                                                          width: isHovered ? '100%' : '0%',
                                                          height: '1px',
                                                          backgroundColor: 'currentColor',
                                                          transition: 'width 200ms ease-in-out'
                                                      }
                                                  }
                                                : undefined
                                        }
                                        data-testid="product-name"
                                    >
                                        {localizedProductName}
                                    </Heading>
                                )}

                                <HideOnDesktop>
                                    {showSwatches && renderSingleColorSwatch()}
                                </HideOnDesktop>
                                {!isProductSet && isWishlistEnabled && showProductLikes && (
                                    <HideOnMobile>
                                        <Flex alignItems="center" gap={0.5}>
                                            <ProductLikes
                                                isFavourite={isProductInWishlist}
                                                wishlistCount={wishlistCount}
                                                isHovered={isHovered}
                                                onFavouriteToggle={handleFavouriteClick}
                                                productName={localizedProductName}
                                                isLoadingFreshLikes={isLoadingFreshLikes}
                                            />
                                        </Flex>
                                    </HideOnMobile>
                                )}
                            </Flex>

                            {isWishlistPage && productWithFilteredVariants?.variationValues && (
                                <Text fontSize="sm">
                                    {productWithFilteredVariants.variationValues.size && (
                                        <>
                                            Size: {productWithFilteredVariants.variationValues.size}{' '}
                                        </>
                                    )}
                                    {productWithFilteredVariants.variationValues.length && (
                                        <>
                                            Length:{' '}
                                            {productWithFilteredVariants.variationValues.length}
                                        </>
                                    )}
                                </Text>
                            )}

                            {!isProductSet &&
                                (isRefreshingData ? (
                                    <PricingAndPromotionsSkeleton />
                                ) : (
                                    <>
                                        {/* Price */}
                                        <Flex {...styles.bodyText} alignItems="flex-start">
                                            <DisplayPrice
                                                priceData={priceData}
                                                currency={currency}
                                                currentPriceProps={{
                                                    fontWeight: 'medium'
                                                }}
                                                listPriceProps={{
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                            {productPromotionObject?.calloutMsg && (
                                                <Tooltip
                                                    isOpen={isOpen}
                                                    aria-label={productPromotionObject?.calloutMsg}
                                                    hasArrow
                                                    arrowShadowColor="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.50"
                                                    label={productPromotionObject?.calloutMsg}
                                                    {...styles.toolTip}
                                                    color="black"
                                                    placement="right"
                                                >
                                                    <IconButton
                                                        onMouseEnter={onOpen}
                                                        onMouseLeave={onClose}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            e.preventDefault()
                                                            onToggle()
                                                        }}
                                                        variant="unstyled"
                                                        h={'auto'}
                                                        minW={'auto'}
                                                        display={'flex'}
                                                        alignSelf={'center'}
                                                        icon={<InfoIcon {...styles.toolTipIcon} />}
                                                        aria-label="Tooltip"
                                                    />
                                                </Tooltip>
                                            )}
                                            {isWishlistPage && isOOS && (
                                                <Text
                                                    fontWeight="medium"
                                                    fontSize={{base: 'xs', lg: 'sm'}}
                                                    textTransform="uppercase"
                                                    color="sale"
                                                    ml={2}
                                                >
                                                    {intl.formatMessage({
                                                        id: 'product_tile.label.oos',
                                                        defaultMessage: 'Sold Out'
                                                    })}
                                                </Text>
                                            )}
                                        </Flex>
                                        {productPromotionObject?.name && (
                                            <Text
                                                {...styles.bodyText}
                                                fontWeight="medium"
                                                fontSize={{base: 'xs', lg: 'sm'}}
                                                color={priceData.isOnSale ? 'black' : 'sale'}
                                            >
                                                {productPromotionObject.name}
                                            </Text>
                                        )}
                                    </>
                                ))}
                            {renderProductCallout()}
                        </Box>
                    </Link>
                    {!isProductSet && enableFavourite && isHydrated() && (
                        <Box
                            onClick={(e) => {
                                e.preventDefault()
                            }}
                        >
                            <IconButtonWithRegistration
                                as={ChakraLink}
                                href={'#'}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                data-wishlist-product-id={productId || id}
                                data-testid="wishlist-button"
                                width={{base: '16px', lg: '20px'}}
                                height={{base: '16px', lg: '20px'}}
                                minWidth={{base: '12px', lg: '20px'}}
                                style={
                                    isDesktop
                                        ? {
                                              top: '6px',
                                              right: '6px'
                                          }
                                        : {
                                              top: '8px',
                                              right: '8px'
                                          }
                                }
                                aria-label={
                                    isProductInWishlist
                                        ? intl.formatMessage(
                                              {
                                                  id: 'product_tile.assistive_msg.remove_from_wishlist',
                                                  defaultMessage: 'Remove {product} from wishlist'
                                              },
                                              {product: localizedProductName}
                                          )
                                        : intl.formatMessage(
                                              {
                                                  id: 'product_tile.assistive_msg.add_to_wishlist',
                                                  defaultMessage: 'Add {product} to wishlist'
                                              },
                                              {product: localizedProductName}
                                          )
                                }
                                icon={
                                    isProductInWishlist ? (
                                        <HeartSolidIcon
                                            position={{base: 'relative'}}
                                            width={{base: '16px', lg: '20px'}}
                                            height={{base: '16px', lg: '20px'}}
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    ) : (
                                        <HeartIcon
                                            display={!isHovered && isDesktop ? 'none' : 'inline'}
                                            position={{base: 'relative'}}
                                            width={{base: '16px', lg: '20px'}}
                                            height={{base: '16px', lg: '20px'}}
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    )
                                }
                                {...styles.favIcon}
                                onClick={() => {
                                    handleFavouriteClick()
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {/* QuickView */}
                {!setMobileQuickViewOpen && (
                    <Drawer
                        placement="bottom"
                        isOpen={mobileQuickViewOpen}
                        onClose={() => setMobileQuickViewOpenInternal(false)}
                        size="sm"
                        motionPreset="slideInBottom"
                        scrollBehavior="inside"
                        isFullHeight={false}
                        returnFocusOnClose={false}
                        height="65%"
                        closeOnOverlayClick={true}
                    >
                        <DrawerOverlay />
                        <DrawerContent
                            marginTop={0}
                            onClick={(e) => {
                                e.stopPropagation()
                            }}
                        >
                            <DrawerBody padding="0">
                                <QuickView
                                    productId={productId}
                                    setIsQuickViewOpen={setMobileQuickViewOpenInternal}
                                    showImageGallery={true}
                                    isFavourite={isProductInWishlist}
                                    wishlistCount={wishlistCount}
                                    setProductId={setProductId}
                                    isWishlistPage={isWishlistPage}
                                    wishlistProductId={wishlistProductId}
                                    disableFocusTrap={true}
                                    searchHitData={product}
                                    onVariantSelect={setSelectedVariantId}
                                />
                            </DrawerBody>
                        </DrawerContent>
                    </Drawer>
                )}
            </>
        )
    })
)

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        productId: PropTypes.string,
        id: PropTypes.string,
        productPromotions: PropTypes.array,
        representedProduct: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variants: PropTypes.array,
        variationGroups: PropTypes.array,
        productType: PropTypes.object,
        type: PropTypes.shape({
            set: PropTypes.bool,
            bundle: PropTypes.bool,
            item: PropTypes.bool
        }),
        c_tilesSwatches: PropTypes.shape({
            count: PropTypes.number,
            swatchURL: PropTypes.string
        })
    }),
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,

    /**
     * Display the product as a favourite.
     */
    showSwatches: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * The `viewType` of the image component. This defaults to 'large'.
     */
    imageViewType: PropTypes.string,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    videoProps: PropTypes.shape({
        preload: PropTypes.oneOf(['none', 'metadata', 'auto'])
    }),
    /**
     * Details of badge labels and the corresponding product custom properties that enable badges.
     */
    badgeDetails: PropTypes.array,
    /**
     * Determines whether to display a skeleton over personalizable data (e.g., pricing and promotions) during data refresh.
     */
    isRefreshingData: PropTypes.bool,
    setQuickViewProduct: PropTypes.func,
    setMobileQuickViewOpen: PropTypes.func,
    setQuickViewWishlistInfo: PropTypes.func,
    showVideo: PropTypes.bool,
    showProductLikes: PropTypes.bool,
    showQV: PropTypes.bool,
    isShopTheLook: PropTypes.bool,
    isOOS: PropTypes.bool,
    isWishlistPage: PropTypes.bool,
    disableImageScroll: PropTypes.bool,
    overwriteProductName: PropTypes.string,
    responsiveMediaBlock: PropTypes.node,
    variant: PropTypes.string,
    onClick: PropTypes.func,
    isLayout: PropTypes.bool,
    /**
     * CMS styling object for product title (extracted to prevent DOM warnings)
     */
    productTitleStyle: PropTypes.object,
    /**
     * CMS styling object for product price (extracted to prevent DOM warnings)
     */
    productPriceStyle: PropTypes.object
}

export default ProductTile

const getProductPromotionIds = (productWithFilteredVariants) => {
    const variants =
        (productWithFilteredVariants.variants &&
            productWithFilteredVariants.variants.find((variant) => variant.productPromotions)) ||
        []

    let productPromotions
    if (variants?.length > 0) {
        productPromotions = variants.productPromotions
    } else {
        productPromotions = productWithFilteredVariants.productPromotions
    }

    return productPromotions?.map((promo) => promo.promotionId) || []
}
